import { useState, useRef, useMemo, useEffect, useLayoutEffect, useCallback, forwardRef, useImperativeHandle, lazy, Suspense } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { updateKeyframe, enhanceImagePrompt } from '../../../lib/api';
import { useScenesStore, useEditorUIStore, useBackgroundTasksStore, TASK_TYPES } from '../../../stores';
import ConfirmationModal from '../../ConfirmationModal';
import TextBox from './TextBox';
import ImageLayer from './ImageLayer';
import './EditingKeyframeView.css';

const WorldExplorerOverlay = lazy(() => import('./WorldExplorerOverlay'));

const MAGIC_SELECT_EDIT_PREFIX = 'Edit the subject in the red rectangle: ';

const EditingKeyframeView = forwardRef(function EditingKeyframeView({ 
  projectId, 
  sceneId, 
  keyframeId,
  keyframeUrl, 
  onComplete = () => {},
  onCancel = () => {},
  onSave = () => {}, // For saving non-pending-preview changes (canvas/layers)
  allAssets = [],
  // Support restoring pending preview when opening from background task notification
  initialPendingUrl = null,
  onClearInitialPendingUrl = () => {},
  // Tool state from context
  activeTool = 'pen',
  onToolChange = () => {},
  penColor = '#ff0000',
  onPenColorChange = () => {},
  textLayers = [],
  onTextLayersChange = () => {},
  imageLayers = [],
  onImageLayersChange = () => {},
  // Prompt state - now controlled by EditorPage
  prompt = '',
  onPromptChange = () => {},
  onPromptSubmit = () => {},
  isGenerating = false,
  mentionableItems = [],
  // Auto-edit prompt to fill when user adds canvas content but prompt is empty
  autoEditPrompt = '',
  autoEditPromptWithCutout = '',
  // Magic select state (SAM-3 segmentation)
  magicSelectState = null,
  onMagicSelectLasso = () => {}, // Changed from onMagicSelectClick - now accepts box coordinates
  onMagicSelectAction = () => {},
  onMagicSelectEditPromptChange = () => {},
  onMagicSelectDuplicate = () => {},
  onMagicSelectCut = () => {},
  onMagicSelectDelete = () => {},
  onMagicSelectEditSubmit = () => {},
  onMagicSelectClear = () => {},
  // Image layer editing
  editingImageLayerId = null,
  onImageLayerEdit = () => {},
  // History (undo/redo)
  historyEntries = [],
  historyIndex = -1,
  onUndoHistory = () => {},
  onRedoHistory = () => {},
  // History callbacks for annotations and layer transforms
  onAnnotationComplete = () => {},
  onLayerTransformComplete = () => {},
  onTextLayerAdded = () => {},
  onTextLayerDeleted = () => {},
  onImageLayerDeleted = () => {},
  // Color adjustments for live preview
  colorAdjustments = { brightness: 0, contrast: 0, saturation: 0, temperature: 0, tint: 0 },
  // Version preview (read-only mode)
  isVersionPreview = false,
  // World explorer (3D world navigation)
  worldExplorerData = null,
  isWorldExploring = false,
  onWorldCapture = () => {},
  onWorldExplorerCancel = () => {},
  isWorldGenerating = false,
  isWorldCapturing = false,
}, ref) {
  // Debug: log when imageLayers prop changes
  useEffect(() => {
    console.log('[EditingKeyframeView] imageLayers prop updated:', imageLayers?.length, imageLayers);
  }, [imageLayers]);

  // Get scenes from store to access keyframes from all scenes
  const scenes = useScenesStore((state) => state.scenes);
  const markKeyframeAsNewlyCompleted = useScenesStore((state) => state.markKeyframeAsNewlyCompleted);
  // Get video cache for motion end frame thumbnails
  const videoCache = useEditorUIStore((state) => state.videoCache);
  const previewingKeyframeVersionNumber = useEditorUIStore((state) => state.previewingKeyframeVersionNumber);
  const imageModel = useEditorUIStore((state) => state.imageEditModel);
  const setImageModel = useEditorUIStore((state) => state.setImageEditModel);
  
  // Background tasks store
  const addTask = useBackgroundTasksStore((state) => state.addTask);
  const completeTask = useBackgroundTasksStore((state) => state.completeTask);
  const failTask = useBackgroundTasksStore((state) => state.failTask);
  const isTaskSentToBackground = useBackgroundTasksStore((state) => state.isTaskSentToBackground);
  const markTaskAsBackground = useBackgroundTasksStore((state) => state.markTaskAsBackground);
  
  const [uploadedImages, setUploadedImages] = useState([]);
  const [error, setError] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const [fittedImageSize, setFittedImageSize] = useState(null); // { width, height } to constrain image to container
  /** While a new `displayUrl` is decoding, keep wrapper dimensions from the last fitted frame to avoid layout jump */
  const [layoutHoldSize, setLayoutHoldSize] = useState(null);
  const lastStableLayoutRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isGeneratingInternal, setIsGeneratingInternal] = useState(false);
  const [isEnhancingEditPrompt, setIsEnhancingEditPrompt] = useState(false);
  const [selectedTextLayerId, setSelectedTextLayerId] = useState(null);
  const [selectedImageLayerId, setSelectedImageLayerId] = useState(null);
  const [isLayerDragging, setIsLayerDragging] = useState(false);
  const updateSceneStore = useScenesStore((state) => state.updateScene);
  
  // Lasso selection state for magic select
  const [isLassoDrawing, setIsLassoDrawing] = useState(false);
  const [lassoPath, setLassoPath] = useState([]); // Array of { x, y } points (canvas coordinates)
  
  // Use external isGenerating or internal state
  const generating = isGenerating || isGeneratingInternal;
  const worldPipelineBusy = isWorldGenerating || isWorldCapturing;
  const isWorldExplorerIdle =
    isWorldExploring && Boolean(worldExplorerData?.spzUrl) && !isWorldGenerating && !isWorldCapturing;
  const uiBlockedForInteraction = generating || isCommitting || worldPipelineBusy;
  const showKeyframeGeneratingOverlay =
    !isVersionPreview && !isWorldExplorerIdle && uiBlockedForInteraction;
  
  // Track if component is still mounted - prevents navigation when user leaves
  const isMountedRef = useRef(true);
  // Track the current task ID for marking as background when user navigates away
  const currentTaskIdRef = useRef(null);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Preview/pending state
  //
  // IMPORTANT:
  // - `keyframeUrl` can be re-used by the backend (same URL string) even when the underlying image changes.
  // - React state updates that set the same string do not trigger a re-render, so the <img> can get "stuck".
  // - We track a revision counter and use it as part of the <img> `key` to force a refresh even when the URL string is unchanged.
  const [originalUrl, setOriginalUrl] = useState(keyframeUrl);
  const [currentUrl, setCurrentUrl] = useState(keyframeUrl);
  const [currentUrlRevision, setCurrentUrlRevision] = useState(0);
  // Initialize from prop if provided (restored from background task)
  const [pendingUrl, setPendingUrl] = useState(initialPendingUrl);
  const [pendingUrlRevision, setPendingUrlRevision] = useState(0);
  const [pendingPrompt, setPendingPrompt] = useState(''); // Prompt used for pending preview
  
  // Clear the initial pending URL after consuming it (prevents re-applying on subsequent renders)
  useEffect(() => {
    if (initialPendingUrl) {
      onClearInitialPendingUrl();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset base/pending URLs when switching to a different keyframe.
  // (Keeps internal state in sync even if the component is not fully unmounted between keyframes.)
  useEffect(() => {
    setOriginalUrl(keyframeUrl);
    setCurrentUrl(keyframeUrl);
    setCurrentUrlRevision((r) => r + 1);
    setPendingUrl(initialPendingUrl);
    setPendingUrlRevision((r) => r + 1);
    setPendingPrompt('');
    hasCutoutRef.current = false;
    lastStableLayoutRef.current = null;
    setLayoutHoldSize(null);
  }, [keyframeId, keyframeUrl, initialPendingUrl]);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Track mentioned assets for linking
  const [mentionedAssets, setMentionedAssets] = useState([]);
  
  // Content policy error state
  const [contentPolicyError, setContentPolicyError] = useState(null);
  
  // Drawing canvas ref
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const worldExplorerOverlayRef = useRef(null);
  const mediaWrapperRef = useRef(null);
  const containerRef = useRef(null);
  const textMeasureCanvasRef = useRef(null);
  
  // Saved state for restore on discard
  const savedCanvasDataRef = useRef(null); // ImageData from canvas before clearing
  const savedCanvasDataUrlRef = useRef(null); // Data URL snapshot fallback (used when ImageData isn't available)
  const savedTextLayersRef = useRef(null); // Text layers before clearing
  const savedImageLayersRef = useRef(null); // Image layers before clearing
  const pendingRestoreRef = useRef(false); // Flag to trigger restore after view switch
  const restorationTimeoutRef = useRef(null); // Track restoration timeout to avoid multiple timeouts
  const discardPendingPreviewRef = useRef(() => {});
  // Track if a cutout (region with transparency) was created via magic select delete
  const hasCutoutRef = useRef(false);

  const hasPendingChanges = pendingUrl !== null;

  // Keyboard shortcut handler for undo/redo (CMD+Z / CMD+SHIFT+Z) and DELETE key
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle when this component is mounted and not generating / world pipeline busy
      if (generating || worldPipelineBusy) return;
      
      // Don't override if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // Check for CMD+Z (Mac) or CTRL+Z (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          // CMD+SHIFT+Z = Redo
          e.preventDefault();
          onRedoHistory();
        } else {
          // CMD+Z = Undo
          e.preventDefault();
          onUndoHistory();
        }
        return;
      }
      
      // DELETE or Backspace key - delete selected layers or magic selection
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Check if any text layer is in editing mode - don't intercept delete while editing text
        const isAnyTextEditing = textLayers.some((l) => l.isEditing);
        if (isAnyTextEditing) {
          return; // Let the text editor handle the key
        }
        
        // Priority 1: Delete selected text layer (not in editing mode)
        if (selectedTextLayerId) {
          e.preventDefault();
          const newLayers = textLayers.filter((l) => l.id !== selectedTextLayerId);
          onTextLayerDeleted(newLayers);
          setSelectedTextLayerId(null);
          return;
        }
        
        // Priority 2: Delete selected image layer
        if (selectedImageLayerId) {
          e.preventDefault();
          const newLayers = imageLayers.filter((l) => l.id !== selectedImageLayerId);
          onImageLayerDeleted(newLayers);
          setSelectedImageLayerId(null);
          return;
        }
        
        // Priority 3: Delete magic selection
        if (magicSelectState?.selections?.length > 0) {
          e.preventDefault();
          onMagicSelectDelete();
          return;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    generating,
    worldPipelineBusy,
    onUndoHistory, 
    onRedoHistory, 
    textLayers, 
    imageLayers, 
    selectedTextLayerId, 
    selectedImageLayerId, 
    magicSelectState, 
    onTextLayerDeleted, 
    onImageLayerDeleted, 
    onMagicSelectDelete
  ]);

  // Determine if canvas is active (drawing or erasing)
  const isCanvasActive = activeTool === 'pen' || activeTool === 'eraser';
  
  // Determine if in selection/cursor mode
  const isCursorMode = activeTool === 'cursor';

  // Compute fitted image dimensions so the image fits fully within the container
  // regardless of aspect ratio (portrait, landscape, or square).
  const computeFittedSize = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;
    const img = imageRef.current;
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    if (naturalW > 0 && naturalH > 0) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const availW = containerRect.width;
      const availH = containerRect.height;
      const scale = Math.min(availW / naturalW, availH / naturalH);
      const w = Math.round(naturalW * scale);
      const h = Math.round(naturalH * scale);
      setFittedImageSize((prev) => {
        if (prev && prev.width === w && prev.height === h) {
          lastStableLayoutRef.current = { width: w, height: h };
          return prev;
        }
        lastStableLayoutRef.current = { width: w, height: h };
        return { width: w, height: h };
      });
    }
  }, []);

  // Update canvas size when image loads (canvas is a transparent overlay, not the background)
  const updateCanvasSize = useCallback(() => {
    if (!imageRef.current || !containerRef.current || !mediaWrapperRef.current) return;

    // First compute fitted dimensions
    computeFittedSize();

    const img = imageRef.current;
    const wrapper = mediaWrapperRef.current;
    const wrapperRect = wrapper.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Account for border: clientLeft/clientTop give border width
    const borderLeft = wrapper.clientLeft;
    const borderTop = wrapper.clientTop;

    // Always use measured position for accurate alignment
    const nextWidth = Math.round(imgRect.width);
    const nextHeight = Math.round(imgRect.height);
    const nextBounds = {
      left: imgRect.left - wrapperRect.left - borderLeft,
      top: imgRect.top - wrapperRect.top - borderTop,
      width: imgRect.width,
      height: imgRect.height,
    };

    // Only update if size actually changed to avoid clearing canvas unnecessarily
    if (canvasSize.width !== nextWidth || canvasSize.height !== nextHeight) {
      setCanvasSize({ width: nextWidth, height: nextHeight });
    }

    setImageBounds(nextBounds);
  }, [canvasSize.width, canvasSize.height, computeFittedSize]);

  // Update canvas size when image loads or pendingUrl changes
  useEffect(() => {
    updateCanvasSize();
  }, [updateCanvasSize]);

  // Recalculate bounds after fitted size is applied (image dimensions changed in DOM)
  useEffect(() => {
    if (fittedImageSize) {
      requestAnimationFrame(() => {
        updateCanvasSize();
      });
    }
  }, [fittedImageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      // Reset fitted size so it gets recalculated for the new container dimensions
      setFittedImageSize(null);
      setLayoutHoldSize(null);
      requestAnimationFrame(() => {
        updateCanvasSize();
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCanvasSize]);

  const initializeCanvas = useCallback(() => {
    setLayoutHoldSize(null);
    updateCanvasSize();
  }, [updateCanvasSize]);

  // Handle canvas drawing
  const handleCanvasMouseDown = useCallback((e) => {
    // If any text layer is being edited, close it first and don't start drawing
    const isAnyTextEditing = textLayers.some((l) => l.isEditing);
    if (isAnyTextEditing) {
      const updatedLayers = textLayers.map((l) => ({ ...l, isEditing: false }));
      onTextLayersChange(updatedLayers);
      setSelectedTextLayerId(null);
      return; // Don't start drawing on this click
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = 20;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = penColor;
      ctx.lineWidth = 3;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, [activeTool, penColor, textLayers, onTextLayersChange]);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isDrawing) return;
      const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing]);

  const handleCanvasMouseUp = useCallback(() => {
    const wasDrawing = isDrawing;
    setIsDrawing(false);
    
    // Auto-fill prompt when user finishes drawing and prompt is empty
    const effectiveAutoEditPrompt =
      hasCutoutRef.current && autoEditPromptWithCutout ? autoEditPromptWithCutout : autoEditPrompt;

    if (effectiveAutoEditPrompt && (!prompt || prompt.trim() === '')) {
      // Small delay to ensure canvas state is updated
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas || canvas.width === 0 || canvas.height === 0) return;
        
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Check if any pixel has non-zero alpha (something was drawn)
        let hasContent = false;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] > 0) {
            hasContent = true;
            break;
          }
        }
        
        if (hasContent) {
          onPromptChange(effectiveAutoEditPrompt);
        }
      }, 10);
    }
    
    // Push to history when annotation stroke is completed
    if (wasDrawing) {
      onAnnotationComplete();
    }
  }, [autoEditPrompt, autoEditPromptWithCutout, prompt, onPromptChange, isDrawing, onAnnotationComplete]);

  // Lasso selection handlers for magic-select tool
  // Lasso coordinates are stored in IMAGE-RELATIVE space (not wrapper space)
  // This ensures alignment with the mask overlay which is also positioned at imageBounds
  const handleLassoMouseDown = useCallback((e) => {
    if (activeTool !== 'magic-select') return;
    
    const wrapper = mediaWrapperRef.current;
    if (!wrapper || !imageBounds.width || !imageBounds.height) return;
    
    const wrapperRect = wrapper.getBoundingClientRect();
    // Account for border: clientLeft/clientTop give border width
    // This converts from border-edge-relative to content-edge-relative coords
    const contentX = e.clientX - wrapperRect.left - wrapper.clientLeft;
    const contentY = e.clientY - wrapperRect.top - wrapper.clientTop;
    
    // Ignore if the pointer starts outside the rendered image bounds.
    if (
      contentX < imageBounds.left ||
      contentX > imageBounds.left + imageBounds.width ||
      contentY < imageBounds.top ||
      contentY > imageBounds.top + imageBounds.height
    ) {
      return;
    }
    
    // Convert to image-relative coordinates
    const imageX = contentX - imageBounds.left;
    const imageY = contentY - imageBounds.top;
    
    // Start lasso drawing (store points in image-relative coordinates)
    setIsLassoDrawing(true);
    setLassoPath([{ x: imageX, y: imageY }]);
  }, [activeTool, imageBounds]);

  const handleLassoMouseMove = useCallback((e) => {
    if (!isLassoDrawing || activeTool !== 'magic-select') return;
    
    const wrapper = mediaWrapperRef.current;
    if (!wrapper || !imageBounds.width || !imageBounds.height) return;
    
    const wrapperRect = wrapper.getBoundingClientRect();
    // Account for border: clientLeft/clientTop give border width
    const contentX = e.clientX - wrapperRect.left - wrapper.clientLeft;
    const contentY = e.clientY - wrapperRect.top - wrapper.clientTop;
    
    // Convert to image-relative and clamp to image bounds
    const imageX = Math.min(imageBounds.width, Math.max(0, contentX - imageBounds.left));
    const imageY = Math.min(imageBounds.height, Math.max(0, contentY - imageBounds.top));
    
    // Add point to lasso path (throttle by checking distance from last point)
    setLassoPath(prev => {
      const lastPoint = prev[prev.length - 1];
      if (lastPoint) {
        const distance = Math.sqrt(
          Math.pow(imageX - lastPoint.x, 2) + Math.pow(imageY - lastPoint.y, 2)
        );
        // Only add point if it's at least 5 pixels away from the last point
        if (distance < 5) return prev;
      }
      return [...prev, { x: imageX, y: imageY }];
    });
  }, [isLassoDrawing, activeTool, imageBounds]);

  const handleLassoMouseUp = useCallback(() => {
    if (!isLassoDrawing || activeTool !== 'magic-select') {
      setIsLassoDrawing(false);
      setLassoPath([]);
      return;
    }
    
    if (!imageBounds.width || !imageBounds.height || lassoPath.length < 3) {
      // Not enough points for a meaningful lasso selection
      setIsLassoDrawing(false);
      setLassoPath([]);
      return;
    }
    
    // Compute bounding box from lasso path (already in image-relative coords)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const point of lassoPath) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
    
    // Clamp bounds to the image rectangle
    minX = Math.max(0, Math.min(imageBounds.width, minX));
    maxX = Math.max(0, Math.min(imageBounds.width, maxX));
    minY = Math.max(0, Math.min(imageBounds.height, minY));
    maxY = Math.max(0, Math.min(imageBounds.height, maxY));
    
    // Convert to normalized coordinates (0-1)
    const normalizedBox = {
      x1: Math.max(0, Math.min(1, minX / imageBounds.width)),
      y1: Math.max(0, Math.min(1, minY / imageBounds.height)),
      x2: Math.max(0, Math.min(1, maxX / imageBounds.width)),
      y2: Math.max(0, Math.min(1, maxY / imageBounds.height)),
    };
    
    // Compute center position for loading animation
    const centerX = imageBounds.left + (minX + maxX) / 2;
    const centerY = imageBounds.top + (minY + maxY) / 2;
    
    // Stop drawing but keep lasso path visible during search
    setIsLassoDrawing(false);
    // Don't clear lassoPath here - keep it visible while searching
    
    // Call the magic select handler with box coordinates
    onMagicSelectLasso(normalizedBox, centerX, centerY);
  }, [isLassoDrawing, activeTool, lassoPath, onMagicSelectLasso]);

  // Clear lasso path when search completes (mask is returned)
  useEffect(() => {
    // Clear lasso when search finishes (isSearching becomes false and we have a result)
    if (!magicSelectState?.isSearching && lassoPath.length > 0 && !isLassoDrawing) {
      setLassoPath([]);
    }
  }, [magicSelectState?.isSearching, lassoPath.length, isLassoDrawing]);

  // Generate SVG path string from lasso points
  const getLassoPathString = useCallback(() => {
    if (lassoPath.length < 2) return '';
    
    const pathParts = lassoPath.map((point, i) => {
      if (i === 0) return `M ${point.x} ${point.y}`;
      return `L ${point.x} ${point.y}`;
    });
    
    // Close the path
    pathParts.push('Z');
    
    return pathParts.join(' ');
  }, [lassoPath]);

  const handleMagicSelectEditSubmitWithPrefix = useCallback(() => {
    const rawPrompt = magicSelectState?.editPrompt || '';
    const trimmed = rawPrompt.trim();
    if (!trimmed) return;

    const alreadyPrefixed =
      trimmed.toLowerCase().startsWith(MAGIC_SELECT_EDIT_PREFIX.toLowerCase());
    const finalPrompt = alreadyPrefixed ? trimmed : `${MAGIC_SELECT_EDIT_PREFIX}${trimmed}`;

    onMagicSelectEditSubmit(finalPrompt);
  }, [magicSelectState?.editPrompt, onMagicSelectEditSubmit]);

  const handleEnhanceEditPrompt = useCallback(async () => {
    const rawPrompt = magicSelectState?.editPrompt || '';
    const trimmed = rawPrompt.trim();
    if (!trimmed || isEnhancingEditPrompt) return;
    try {
      setIsEnhancingEditPrompt(true);
      const response = await enhanceImagePrompt({
        text: trimmed,
        projectId,
      });
      if (response?.enhanced_prompt) {
        onMagicSelectEditPromptChange(response.enhanced_prompt);
      }
    } catch (err) {
      setError('Failed to enhance prompt. Please try again.');
    } finally {
      setIsEnhancingEditPrompt(false);
    }
  }, [magicSelectState?.editPrompt, isEnhancingEditPrompt, onMagicSelectEditPromptChange, projectId]);

  // Save canvas as annotation
  const saveCanvasAsAnnotation = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
            const file = new File([blob], 'annotation.png', { type: 'image/png' });
            const previewUrl = URL.createObjectURL(blob);
        resolve({
              id: Date.now() + Math.random(),
              file,
              preview: previewUrl,
              name: 'annotation.png',
              isAnnotation: true
        });
        }, 'image/png');
    });
  }, []);

  // Render composite canvas with all layers (base image + drawings + text + image layers)
  const renderCompositeCanvas = useCallback(async () => {
    console.log('[renderCompositeCanvas] Starting render with:', {
      textLayersCount: textLayers?.length || 0,
      imageLayersCount: imageLayers?.length || 0,
      imageLayers: imageLayers?.map(l => ({ id: l.id, imageUrl: l.imageUrl?.substring(0, 50) })),
      colorAdjustments,
    });
    
    const img = imageRef.current;
    if (!img) {
      console.warn('[renderCompositeCanvas] No image ref available');
      return null;
    }

    // Create offscreen canvas at the image's natural resolution
    const offscreenCanvas = document.createElement('canvas');
    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;
    offscreenCanvas.width = naturalWidth;
    offscreenCanvas.height = naturalHeight;
    const ctx = offscreenCanvas.getContext('2d');

    // Build filter string for color adjustments (to apply to base image)
    const { brightness = 0, contrast = 0, saturation = 0, temperature = 0, tint = 0 } = colorAdjustments || {};
    const hasColorAdjustments = brightness !== 0 || contrast !== 0 || saturation !== 0 || temperature !== 0 || tint !== 0;
    
    let filterString = '';
    if (hasColorAdjustments) {
      const filters = [];
      if (brightness !== 0) filters.push(`brightness(${1 + brightness / 100})`);
      if (contrast !== 0) filters.push(`contrast(${1 + contrast / 100})`);
      if (saturation !== 0) filters.push(`saturate(${1 + saturation / 100})`);
      if (temperature !== 0) {
        if (temperature > 0) {
          filters.push(`sepia(${Math.abs(temperature) / 200})`);
        } else {
          filters.push(`hue-rotate(${temperature * 0.3}deg)`);
        }
      }
      if (tint !== 0) {
        filters.push(`hue-rotate(${tint * 0.5}deg)`);
      }
      filterString = filters.join(' ');
    }

    // 1. Draw the base keyframe image with color adjustments applied
    // Need to load it fresh with crossOrigin to avoid tainted canvas
    // Use pendingUrl || currentUrl directly (same as displayUrl which is defined later)
    const imageUrl = pendingUrl || currentUrl || '';
    await new Promise((resolve, reject) => {
      const baseImg = new Image();
      baseImg.crossOrigin = 'anonymous';
      baseImg.onload = () => {
        // Apply color adjustments filter to base image
        if (filterString) {
          ctx.filter = filterString;
        }
        ctx.drawImage(baseImg, 0, 0, naturalWidth, naturalHeight);
        // Reset filter for subsequent drawing operations
        ctx.filter = 'none';
        resolve();
      };
      baseImg.onerror = reject;
      baseImg.src = imageUrl;
    });

    // 2. Calculate scale factors (rendered size vs natural size)
    const scaleX = canvasSize.width > 0 ? naturalWidth / canvasSize.width : 1;
    const scaleY = canvasSize.height > 0 ? naturalHeight / canvasSize.height : 1;

    // 3. Draw pen/eraser overlay if exists and has content
    if (canvasRef.current && canvasSize.width > 0 && canvasSize.height > 0) {
      // The drawing canvas is at rendered size, scale it up to natural size
      ctx.drawImage(canvasRef.current, 0, 0, naturalWidth, naturalHeight);
    }

    // 4. Render image layers with their transforms (position, scale, rotation)
    if (imageLayers && imageLayers.length > 0) {
      for (const layer of imageLayers) {
        if (!layer.imageUrl) continue;
        
        try {
          // Load image layer with crossOrigin to avoid tainted canvas
          const layerImg = await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = layer.imageUrl;
          });
          
          // Image layers store positions in original image coordinates (e.g., 1920x1080)
          // Use UNIFORM scaling (width-based) to match ImageLayer.js preview behavior
          // ImageLayer uses: scaleFactor = containerBounds.width / originalImageWidth for ALL dimensions
          const originalImgWidth = layer.originalImageWidth || 1920;
          const imgScale = naturalWidth / originalImgWidth;
          
          // Calculate position and size at natural resolution using uniform scale
          const x = (layer.position?.x || 0) * imgScale;
          const y = (layer.position?.y || 0) * imgScale;
          const width = (layer.width || 100) * imgScale * (layer.scale || 1);
          const height = (layer.height || 100) * imgScale * (layer.scale || 1);
          const rotation = layer.rotation || 0;
          
          // Check if this is a cropped image (from magic select with mask)
          const hasCrop = layer.cropX !== undefined && layer.cropY !== undefined;
          
          ctx.save();
          ctx.translate(x + width / 2, y + height / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          
          if (hasCrop) {
            // For cropped images, draw only the cropped region
            // The source image is full-size, but we only want to draw the cropped portion
            const cropX = layer.cropX || 0;
            const cropY = layer.cropY || 0;
            const cropWidth = layer.cropWidth || layer.width;
            const cropHeight = layer.cropHeight || layer.height;
            
            ctx.drawImage(
              layerImg,
              cropX, cropY, cropWidth, cropHeight, // Source rect (in original image)
              -width / 2, -height / 2, width, height // Dest rect (in canvas)
            );
          } else {
            // For regular images, use object-fit: contain behavior to match CSS preview
            const imgNatWidth = layerImg.naturalWidth || layerImg.width;
            const imgNatHeight = layerImg.naturalHeight || layerImg.height;
            
            if (imgNatWidth > 0 && imgNatHeight > 0 && width > 0 && height > 0) {
              // Calculate scale to fit image within bounds while preserving aspect ratio
              const containScale = Math.min(width / imgNatWidth, height / imgNatHeight);
              const drawWidth = imgNatWidth * containScale;
              const drawHeight = imgNatHeight * containScale;
              
              // Center within the bounding box (matching object-fit: contain)
              const offsetX = (width - drawWidth) / 2;
              const offsetY = (height - drawHeight) / 2;
              
              ctx.drawImage(layerImg, -width / 2 + offsetX, -height / 2 + offsetY, drawWidth, drawHeight);
            } else {
              // Fallback: draw stretched
              ctx.drawImage(layerImg, -width / 2, -height / 2, width, height);
            }
          }
          
          ctx.restore();
          
          console.log('[renderCompositeCanvas] Drew image layer:', layer.id, {
            position: { x, y },
            size: { width, height },
            hasCrop,
            scale: layer.scale,
            rotation
          });
        } catch (err) {
          console.warn('[renderCompositeCanvas] Failed to load image layer:', layer.id, err);
        }
      }
    }

    // 5. Render text layers with their styles
    if (textLayers && textLayers.length > 0) {
      textLayers.forEach((layer) => {
        if (!layer.content) return;
        
        const style = layer.style || {};
        const fontSize = (style.fontSize || 24) * scaleY;
        const fontWeight = style.fontWeight || 'normal';
        const fontStyle = style.fontStyle || 'normal';
        const fontFamily = style.fontFamily || 'Inter, sans-serif';
        const lineHeight = fontSize * 1.2; // Standard line height multiplier
        
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = style.color || '#000000';
        ctx.textBaseline = 'top';
        
        // Scale position from rendered size to natural size
        const x = (layer.position?.x || 0) * scaleX;
        let y = (layer.position?.y || 0) * scaleY;
        
        // Split text by newlines and render each line
        const lines = layer.content.split('\n');
        lines.forEach((line) => {
          ctx.fillText(line, x, y);
          y += lineHeight;
        });
      });
    }

    // 6. Export as PNG File
    return new Promise((resolve) => {
      offscreenCanvas.toBlob((blob) => {
        if (!blob) {
          console.warn('[renderCompositeCanvas] Failed to create blob');
          resolve(null);
          return;
        }
        const file = new File([blob], 'annotated-keyframe.png', { type: 'image/png' });
        resolve(file);
      }, 'image/png');
    });
  }, [pendingUrl, currentUrl, canvasSize, textLayers, imageLayers, colorAdjustments]);

  // Clear the drawing canvas
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Save canvas state before clearing (for restore on discard)
  const saveCanvasState = useCallback(() => {
    const canvas = canvasRef.current;
    console.log('[saveCanvasState] Canvas ref:', canvas, 'width:', canvas?.width, 'height:', canvas?.height);
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      console.log('[saveCanvasState] No canvas or zero size, not saving');
      savedCanvasDataRef.current = null;
      savedCanvasDataUrlRef.current = null;
      return;
    }
    const ctx = canvas.getContext('2d');
    savedCanvasDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    console.log('[saveCanvasState] Saved ImageData:', savedCanvasDataRef.current?.width, 'x', savedCanvasDataRef.current?.height);

    // Also store a data URL snapshot as a fallback for restoration (e.g., after history navigation)
    try {
      const imageData = savedCanvasDataRef.current;
      const data = imageData?.data;
      let hasContent = false;
      if (data) {
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] > 0) {
            hasContent = true;
            break;
          }
        }
      }
      savedCanvasDataUrlRef.current = hasContent ? canvas.toDataURL('image/png') : null;
    } catch (err) {
      savedCanvasDataUrlRef.current = null;
    }
  }, []);

  // Restore canvas state from saved data
  const restoreCanvasState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !savedCanvasDataRef.current) return;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(savedCanvasDataRef.current, 0, 0);
  }, []);

  // Set pending preview URL (called when new keyframe comes back from API)
  // Note: We save textLayers from the prop directly, not from the parameter,
  // because the parameter might be stale due to React closure issues
  const setPendingPreview = useCallback((url, promptText, currentTextLayers, currentImageLayers) => {
    // Prefer the latest props, but fall back to the arguments (which may come from an async closure).
    const textLayersToSave = textLayers && textLayers.length > 0 ? textLayers : currentTextLayers;
    const imageLayersToSave = imageLayers && imageLayers.length > 0 ? imageLayers : currentImageLayers;

    console.log('[setPendingPreview] Called with url:', url, {
      paramTextLayers: currentTextLayers?.length,
      propTextLayers: textLayers?.length,
      paramImageLayers: currentImageLayers?.length,
      propImageLayers: imageLayers?.length,
    });
    // Save current state before clearing (for restore on discard)
    saveCanvasState();
    savedTextLayersRef.current = textLayersToSave ? [...textLayersToSave] : [];
    savedImageLayersRef.current = imageLayersToSave ? [...imageLayersToSave] : [];
    console.log('[setPendingPreview] Saved text layers:', savedTextLayersRef.current?.length, savedTextLayersRef.current);
    
    setPendingUrl(url);
    setPendingUrlRevision((r) => r + 1);
    setPendingPrompt(promptText || '');
    // Clear overlays since annotations/layers are now baked into the preview image
    clearCanvas();

    // Clear overlay layers to prevent double-rendering. They'll be restored if the user discards.
    if (typeof onTextLayersChange === 'function') onTextLayersChange([]);
    if (typeof onImageLayersChange === 'function') onImageLayersChange([]);
    setSelectedTextLayerId(null);
    setSelectedImageLayerId(null);
  }, [saveCanvasState, clearCanvas, textLayers, imageLayers, onTextLayersChange, onImageLayersChange]);

  // Check if canvas has any drawings (non-empty pixels)
  const hasCanvasContent = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return false;
    
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Check if any pixel has non-zero alpha (i.e., something was drawn)
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  }, []);

  // Check if current image has a cutout (transparent regions from magic select delete)
  const hasCutout = useCallback(() => {
    return hasCutoutRef.current;
  }, []);

  const hasColorAdjustments = useCallback(() => {
    const { brightness = 0, contrast = 0, saturation = 0, temperature = 0, tint = 0 } = colorAdjustments || {};
    return brightness !== 0 || contrast !== 0 || saturation !== 0 || temperature !== 0 || tint !== 0;
  }, [colorAdjustments]);

  // Get the natural dimensions of the currently displayed base keyframe image.
  // This is used by the parent (EditorPage) to insert image layers without distortion.
  const getBaseImageDimensions = useCallback(() => {
    const img = imageRef.current;
    if (!img) return null;
    const width = img.naturalWidth || img.width || 0;
    const height = img.naturalHeight || img.height || 0;
    if (!width || !height) return null;
    return { width, height };
  }, []);

  // Update current URL directly (used when deleting a region and committing immediately)
  // When called, marks that a cutout was created (image has transparent regions)
  const updateCurrentUrl = useCallback((newUrl, isCutout = true) => {
    setCurrentUrl(newUrl);
    setCurrentUrlRevision((r) => r + 1);
    // Track whether the current base image has transparency/cutouts.
    // Passing `false` explicitly clears the cutout flag.
    hasCutoutRef.current = !!isCutout;
  }, []);

  // Get the current URL (may have cutouts applied)
  const getCurrentUrl = useCallback(() => {
    return currentUrl;
  }, [currentUrl]);

  // Get the currently displayed URL (pending preview takes precedence).
  // This is the image the user actually sees and interacts with.
  const getDisplayedUrl = useCallback(() => {
    return pendingUrl || currentUrl;
  }, [pendingUrl, currentUrl]);

  // Expose methods to parent via ref
  // Get canvas drawing as a data URL for history snapshots
  const getCanvasDataUrl = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return null;
    
    // Check if canvas has any content first
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    let hasContent = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) {
        hasContent = true;
        break;
      }
    }
    
    if (!hasContent) return null;
    return canvas.toDataURL('image/png');
  }, []);

  // Restore canvas from a data URL
  const restoreCanvasDataUrl = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    if (!dataUrl) {
      // Clear the canvas if no data URL
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataUrl;
  }, []);

  // Check if there are any unsaved changes (for exit confirmation)
  // Returns true if: pending preview, URL changed from original, canvas drawings, or layers exist
  const hasUnsavedChanges = useCallback(() => {
    // Check pending preview (from prompt submission)
    if (pendingUrl !== null) return true;
    // Check if URL changed from original (e.g., via magic select delete)
    if (currentUrl !== originalUrl) return true;
    // Check canvas drawings
    if (hasCanvasContent()) return true;
    // Check live color adjustments
    if (hasColorAdjustments()) return true;
    // Check text/image layers
    if (textLayers.length > 0 || imageLayers.length > 0) return true;
    return false;
  }, [pendingUrl, currentUrl, originalUrl, hasCanvasContent, hasColorAdjustments, textLayers, imageLayers]);

  // Request to exit - shows confirmation modal if there are unsaved changes
  const requestExit = useCallback(() => {
    setShowConfirmModal(true);
  }, []);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    renderCompositeCanvas,
    clearCanvas,
    setPendingPreview,
    hasCanvasContent,
    hasCutout,
    hasColorAdjustments,
    getBaseImageDimensions,
    updateCurrentUrl,
    getCurrentUrl,
    getDisplayedUrl,
    // Used by EditorPage "Save" action to commit the exact preview image (without re-rendering to canvas)
    hasPendingPreview: () => pendingUrl !== null,
    getPendingPreview: () => (pendingUrl ? { url: pendingUrl, prompt: pendingPrompt } : null),
    getCanvasDataUrl,
    restoreCanvasDataUrl,
    // History integration: restore/clear pending preview without running the "setPendingPreview" side-effects.
    setPendingPreviewFromHistory: (url, promptText = '') => {
      setPendingUrl(url || null);
      setPendingPrompt(promptText || '');
    },
    discardPendingPreview: () => {
      discardPendingPreviewRef.current?.();
    },
    // History integration: prime discard-restore refs so "Discard" works after undo/redo.
    setDiscardRestoreState: (state) => {
      savedCanvasDataRef.current = null;
      savedCanvasDataUrlRef.current = state?.canvasDataUrl || null;
      savedTextLayersRef.current = state?.textLayers ? [...state.textLayers] : [];
      savedImageLayersRef.current = state?.imageLayers ? [...state.imageLayers] : [];
    },
    clearDiscardRestoreState: () => {
      savedCanvasDataRef.current = null;
      savedCanvasDataUrlRef.current = null;
      savedTextLayersRef.current = null;
      savedImageLayersRef.current = null;
      pendingRestoreRef.current = false;
    },
    // For exit confirmation flow
    hasUnsavedChanges,
    requestExit,
    // Sidebar "Capture" calls onWorldCapture with a click event — EditorPage delegates here to grab a real data URL.
    captureWorldExplorer: () => worldExplorerOverlayRef.current?.captureScreenshot?.(),
  }), [
    renderCompositeCanvas,
    clearCanvas,
    setPendingPreview,
    hasCanvasContent,
    hasCutout,
    hasColorAdjustments,
    getBaseImageDimensions,
    updateCurrentUrl,
    getCurrentUrl,
    getDisplayedUrl,
    pendingUrl,
    pendingPrompt,
    getCanvasDataUrl,
    restoreCanvasDataUrl,
    hasUnsavedChanges,
    requestExit,
  ]);

  // Handle text tool click on canvas
  const handleCanvasClick = useCallback((e) => {
    // Always close any editing text layers when clicking on canvas
    const isAnyTextEditing = textLayers.some((l) => l.isEditing);
    if (isAnyTextEditing || selectedTextLayerId) {
      setSelectedTextLayerId(null);
      const updatedLayers = textLayers.map((l) => ({ ...l, isEditing: false }));
      onTextLayersChange(updatedLayers);
    }
    
    // Deselect any selected image layer when clicking on canvas background
    if (selectedImageLayerId) {
      setSelectedImageLayerId(null);
    }
    
    // Magic-select now uses lasso drawing (handled by handleLassoMouseDown/Move/Up)
    // Don't process clicks for magic-select here
    if (activeTool === 'magic-select') {
      return;
    }
    
    if (activeTool !== 'text') return;
    
    const img = imageRef.current;
    if (!img) return;
    
    // Calculate position relative to the actual image (not container)
    // This accounts for object-fit: contain centering
    const imageRect = img.getBoundingClientRect();
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;
    
    // Create a new text layer at click position (relative to image)
    const newTextLayer = {
      id: `text-${Date.now()}`,
      content: 'New text',
      position: { x, y },
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 24,
        fontWeight: 'normal',
        fontStyle: 'normal',
        color: '#000000',
        textDecoration: 'none',
      },
      isEditing: true,
    };
    
    const newLayers = [...textLayers.map((l) => ({ ...l, isEditing: false })), newTextLayer];
    // Use onTextLayerAdded which also pushes to history
    onTextLayerAdded(newLayers);
    setSelectedTextLayerId(newTextLayer.id);
    
    // Return to cursor tool after adding text
    if (onToolChange) {
      onToolChange('cursor');
    }
  }, [activeTool, textLayers, onTextLayerAdded, selectedTextLayerId, selectedImageLayerId, onToolChange]);

  // Display URL is pending if exists, otherwise current
  const displayUrl = pendingUrl || currentUrl;
  const displayUrlRevision = pendingUrl ? pendingUrlRevision : currentUrlRevision;

  useLayoutEffect(() => {
    const img = imageRef.current;
    const stable = lastStableLayoutRef.current;
    if (!stable) return;
    if (img?.complete && img.naturalWidth > 0) {
      setLayoutHoldSize(null);
      return;
    }
    setLayoutHoldSize({ width: stable.width, height: stable.height });
  }, [displayUrl, displayUrlRevision]);

  // Calculate CSS filter string for live preview of color adjustments
  // Temperature and tint are approximated using sepia/hue-rotate for CSS preview
  const imageFilterStyle = useMemo(() => {
    const { brightness = 0, contrast = 0, saturation = 0, temperature = 0, tint = 0 } = colorAdjustments || {};
    
    // Only apply filter if there are non-zero adjustments
    const hasAdjustments = brightness !== 0 || contrast !== 0 || saturation !== 0 || temperature !== 0 || tint !== 0;
    if (!hasAdjustments) return {};
    
    // Build CSS filter string
    // brightness: 0 = 100%, -100 = 0%, +100 = 200%
    // contrast: 0 = 100%, -100 = 0%, +100 = 200%
    // saturation: 0 = 100%, -100 = 0%, +100 = 200%
    const filters = [];
    
    if (brightness !== 0) {
      filters.push(`brightness(${1 + brightness / 100})`);
    }
    if (contrast !== 0) {
      filters.push(`contrast(${1 + contrast / 100})`);
    }
    if (saturation !== 0) {
      filters.push(`saturate(${1 + saturation / 100})`);
    }
    
    // Temperature: warm (+) adds orange/sepia, cold (-) adds blue via hue-rotate
    // Approximate with sepia + hue-rotate combination
    if (temperature !== 0) {
      if (temperature > 0) {
        // Warm: add sepia tint
        filters.push(`sepia(${Math.abs(temperature) / 200})`);
      } else {
        // Cold: shift hue toward blue
        filters.push(`hue-rotate(${temperature * 0.3}deg)`);
      }
    }
    
    // Tint: green (-) to magenta (+) via hue-rotate
    if (tint !== 0) {
      // Shift hue: negative = green shift, positive = magenta shift
      filters.push(`hue-rotate(${tint * 0.5}deg)`);
    }
    
    return { filter: filters.join(' ') };
  }, [colorAdjustments]);

  const getTextLayerDimensions = useCallback((layer) => {
    if (!layer) return { width: 0, height: 0 };
    if (!textMeasureCanvasRef.current) {
      textMeasureCanvasRef.current = document.createElement('canvas');
    }
    const ctx = textMeasureCanvasRef.current.getContext('2d');
    if (!ctx) return { width: 0, height: 0 };
    const style = layer.style || {};
    const fontSize = style.fontSize || 24;
    const fontWeight = style.fontWeight || 'normal';
    const fontStyle = style.fontStyle || 'normal';
    const fontFamily = style.fontFamily || 'Inter, sans-serif';
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    const lines = (layer.content || '').split('\n');
    let maxWidth = 0;
    lines.forEach((line) => {
      const metrics = ctx.measureText(line || ' ');
      maxWidth = Math.max(maxWidth, metrics.width);
    });
    const lineHeight = fontSize * 1.2;
    const height = Math.max(lineHeight * Math.max(lines.length, 1), lineHeight);
    const width = Math.max(maxWidth, fontSize);
    return { width, height };
  }, []);

  const snapLines = useMemo(() => {
    const hasSelection = selectedTextLayerId || selectedImageLayerId;
    if (!hasSelection || !isLayerDragging || !imageBounds.width || !imageBounds.height) {
      return { showVertical: false, showHorizontal: false, centerX: 0, centerY: 0 };
    }
    const centerX = imageBounds.width / 2;
    const centerY = imageBounds.height / 2;
    let layerCenterX = null;
    let layerCenterY = null;
    if (selectedTextLayerId) {
      const layer = textLayers.find((item) => item.id === selectedTextLayerId);
      if (!layer) return { showVertical: false, showHorizontal: false, centerX, centerY };
      const { width, height } = getTextLayerDimensions(layer);
      const posX = layer.position?.x || 0;
      const posY = layer.position?.y || 0;
      layerCenterX = posX + width / 2;
      layerCenterY = posY + height / 2;
    } else if (selectedImageLayerId) {
      const layer = imageLayers.find((item) => item.id === selectedImageLayerId);
      if (!layer) return { showVertical: false, showHorizontal: false, centerX, centerY };
      const originalWidth = layer.originalImageWidth || imageBounds.width;
      const originalHeight = layer.originalImageHeight || imageBounds.height;
      const scaleFactor = originalWidth > 0 ? imageBounds.width / originalWidth : 1;
      const layerScale = layer.scale || 1;
      const width = (layer.width || 100) * scaleFactor * layerScale;
      const height = (layer.height || 100) * scaleFactor * layerScale;
      const posX = (layer.position?.x || 0) * scaleFactor;
      const posY = (layer.position?.y || 0) * scaleFactor;
      layerCenterX = posX + width / 2;
      layerCenterY = posY + height / 2;
    }
    if (layerCenterX === null || layerCenterY === null) {
      return { showVertical: false, showHorizontal: false, centerX, centerY };
    }
    const SNAP_THRESHOLD = 6;
    return {
      showVertical: Math.abs(layerCenterX - centerX) <= SNAP_THRESHOLD,
      showHorizontal: Math.abs(layerCenterY - centerY) <= SNAP_THRESHOLD,
      centerX,
      centerY,
    };
  }, [
    selectedTextLayerId,
    selectedImageLayerId,
    textLayers,
    imageLayers,
    imageBounds,
    isLayerDragging,
    getTextLayerDimensions,
  ]);

  // Accept pending changes - commit to DB
  const handleAcceptChanges = async () => {
    if (!pendingUrl) return;
    
    setIsCommitting(true);
    setError(null);
    
    try {
      const assetMentionUrls = mentionedAssets
        .filter(asset => asset.imageUrl)
        .map(asset => asset.imageUrl);
      
      const response = await updateKeyframe(
        projectId,
        sceneId,
        keyframeId,
        pendingPrompt,
        [],
        { 
          commitUrl: pendingUrl,
          referenceImageUrls: assetMentionUrls.length > 0 ? assetMentionUrls : null,
          model: imageModel || 'nano-banana-2',
        }
      );

      const nextKeyframeUrl = response.keyframeUrl || response.keyframe?.keyframeUrl;
      const nextKeyframeId = response.keyframeId || response.keyframe?.id || keyframeId;

      if (response.success && nextKeyframeUrl) {
        if (sceneId && keyframeId && updateSceneStore) {
          updateSceneStore(sceneId, (scene) => {
            if (!scene) return scene;

            const targetKeyframeId = nextKeyframeId;
            const existingKeyframes = Array.isArray(scene.keyframes) ? scene.keyframes : [];
            const updatedKeyframes = existingKeyframes.map((kf) => {
              if (kf.id !== targetKeyframeId) return kf;

              if (response.keyframe) {
                return { ...kf, ...response.keyframe };
              }

              return {
                ...kf,
                keyframeUrl: nextKeyframeUrl,
                prompt: pendingPrompt,
              };
            });

            const sceneUpdates = {
              ...scene,
              keyframes: updatedKeyframes,
            };

            if (response.elementSequence) {
              sceneUpdates.elementSequence = response.elementSequence;
            }

            // Note: Do NOT set requiresApproval - editing a keyframe shouldn't trigger approval flow.
            // The approval flow is triggered separately when user explicitly approves the storyboard.

            return sceneUpdates;
          });
        }

        setCurrentUrl(nextKeyframeUrl);
        setCurrentUrlRevision((r) => r + 1);
        setPendingUrl(null);
        setPendingPrompt('');
        setMentionedAssets([]);
        
        // Clear saved state since changes are committed
        savedCanvasDataRef.current = null;
        savedCanvasDataUrlRef.current = null;
        savedTextLayersRef.current = null;
        savedImageLayersRef.current = null;
        pendingRestoreRef.current = false;
        hasCutoutRef.current = false;
        
        // Navigate back to SceneStoryboardView after accepting changes
        onComplete();
      } else {
        throw new Error('Failed to commit changes');
      }
    } catch (err) {
      console.error('Failed to accept changes:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsCommitting(false);
    }
  };

  // Discard pending changes and restore previous annotations
  const handleDiscardChanges = () => {
    console.log('[handleDiscardChanges] Discarding, savedCanvasData:', savedCanvasDataRef.current?.width, 'x', savedCanvasDataRef.current?.height);
    console.log('[handleDiscardChanges] savedTextLayers:', savedTextLayersRef.current);
    console.log('[handleDiscardChanges] savedImageLayers:', savedImageLayersRef.current);
    
    // Set flag to restore after view switches (canvas will be re-mounted)
    pendingRestoreRef.current = true;
    
    // Restore text layers immediately (they're managed via state, not canvas)
    // Always restore even if empty array, to properly reset state
    if (savedTextLayersRef.current !== null) {
      console.log('[handleDiscardChanges] Restoring text layers:', savedTextLayersRef.current);
      onTextLayersChange([...savedTextLayersRef.current]);
    }

    // Restore image layers immediately
    if (savedImageLayersRef.current !== null) {
      console.log('[handleDiscardChanges] Restoring image layers:', savedImageLayersRef.current);
      onImageLayersChange([...savedImageLayersRef.current]);
    }

    // If the user discards the proposed preview, keep the prompt box value (restore the prompt used
    // to generate the preview) so they can tweak and resubmit. Only restore if the prompt box is
    // currently empty to avoid overwriting new user input typed while the preview is showing.
    if ((!prompt || prompt.trim() === '') && pendingPrompt && pendingPrompt.trim() !== '') {
      onPromptChange(pendingPrompt);
    }
    
    setPendingUrl(null);
    setPendingUrlRevision((r) => r + 1);
    setPendingPrompt('');
    setUploadedImages([]);
    setMentionedAssets([]);
    hasCutoutRef.current = false;
  };

  discardPendingPreviewRef.current = handleDiscardChanges;
  
  // Effect to restore canvas after view switches back from comparison to single view
  // Triggers when hasPendingChanges becomes false and canvas size is ready
  useEffect(() => {
    console.log('[restoreEffect] hasPendingChanges:', hasPendingChanges, 'pendingRestore:', pendingRestoreRef.current, 'canvasSize:', canvasSize);
    
    // Only proceed if we're switching back to single view and need to restore
    if (!hasPendingChanges && pendingRestoreRef.current) {
      // Clear any existing timeout to avoid multiple restoration attempts
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
        restorationTimeoutRef.current = null;
      }
      
      // Only attempt restoration if canvas size is ready
      if (canvasSize.width > 0 && canvasSize.height > 0) {
        console.log('[restoreEffect] Conditions met, will restore in 100ms');
        
        // Use a slightly longer delay to ensure the canvas element is fully mounted and sized
        restorationTimeoutRef.current = setTimeout(() => {
          console.log('[restoreEffect] Timeout fired, canvasRef:', canvasRef.current, 'savedData:', savedCanvasDataRef.current?.width, 'x', savedCanvasDataRef.current?.height);
          
          if (canvasRef.current && savedCanvasDataRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const savedData = savedCanvasDataRef.current;
            
            console.log('[restoreEffect] Restoring canvas. Saved:', savedData.width, 'x', savedData.height, 'Current:', canvas.width, 'x', canvas.height);
            
            // If dimensions match, restore directly
            if (savedData.width === canvas.width && savedData.height === canvas.height) {
              ctx.putImageData(savedData, 0, 0);
              console.log('[restoreEffect] Restored directly with putImageData');
            } else {
              // If dimensions differ, use a temp canvas to scale the drawing
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = savedData.width;
              tempCanvas.height = savedData.height;
              const tempCtx = tempCanvas.getContext('2d');
              tempCtx.putImageData(savedData, 0, 0);
              
              // Draw scaled onto the actual canvas
              ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
              console.log('[restoreEffect] Restored with scaling');
            }
          } else if (savedCanvasDataUrlRef.current) {
            // Fallback: restore from a saved data URL snapshot (used after history navigation)
            console.log('[restoreEffect] Restoring canvas from data URL fallback');
            restoreCanvasDataUrl(savedCanvasDataUrlRef.current);
          } else {
            console.log('[restoreEffect] Missing canvasRef or savedData, cannot restore');
          }
          
          // Clear saved state after restoration
          savedCanvasDataRef.current = null;
          savedCanvasDataUrlRef.current = null;
          savedTextLayersRef.current = null;
          savedImageLayersRef.current = null;
          pendingRestoreRef.current = false;
          restorationTimeoutRef.current = null;
        }, 150); // Slightly longer delay to ensure DOM is stable
      }
    }
    
    // Cleanup on unmount only - don't cancel on re-runs to allow restoration to complete
    return () => {
      // Only clear on actual unmount, not on re-runs
      // We track this via the ref - if pendingRestoreRef is false, restoration completed
    };
  }, [hasPendingChanges, canvasSize.width, canvasSize.height]);
  
  // Cleanup restoration timeout on unmount
  useEffect(() => {
    return () => {
      if (restorationTimeoutRef.current) {
        clearTimeout(restorationTimeoutRef.current);
        restorationTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle Done button click (called from sidebar via onComplete)
  const handleDoneClick = useCallback(() => {
    // If currently generating, allow navigation back - task will continue in background
    if (generating) {
      if (currentTaskIdRef.current) {
        markTaskAsBackground(currentTaskIdRef.current);
      }
      onComplete();
      return;
    }
    if (hasPendingChanges) {
      setShowConfirmModal(true);
    } else {
      onComplete();
    }
  }, [generating, hasPendingChanges, markTaskAsBackground, onComplete]);

  // Modal actions
  const handleModalAccept = async () => {
    setShowConfirmModal(false);
    // Always use onSave which routes to handleSaveKeyframe in EditorPage.
    // This properly handles both video-centric (no sceneId) and scene-centric keyframes.
    // handleSaveKeyframe checks for pending preview via getPendingPreview() and updates
    // the correct store (upsertProjectKeyframe for video-centric, updateSceneStore for scene-centric).
    onSave();
  };

  const handleModalDiscard = () => {
    setShowConfirmModal(false);
    handleDiscardChanges();
    onComplete();
  };

  const handleModalCancel = () => {
    setShowConfirmModal(false);
  };

  const handleLayerTransformStart = useCallback(() => {
    setIsLayerDragging(true);
  }, []);

  const handleLayerTransformComplete = useCallback((layerType, action) => {
    setIsLayerDragging(false);
    if (typeof onLayerTransformComplete === 'function') {
      onLayerTransformComplete(layerType, action);
    }
  }, [onLayerTransformComplete]);

  useEffect(() => {
    setIsLayerDragging(false);
  }, [selectedTextLayerId, selectedImageLayerId]);

  // Get cursor style based on active tool
  const getCursorStyle = () => {
    if (activeTool === 'cursor') return 'default';
    if (activeTool === 'magic-select') return 'crosshair';
    if (activeTool === 'pen') return 'crosshair';
    if (activeTool === 'eraser') return 'cell';
    if (activeTool === 'text') return 'text';
    return 'default';
  };

  // Calculate mask overlay position based on canvas size and image dimensions
  const getMaskOverlayStyle = useCallback(() => {
    if (!magicSelectState?.bounds || !magicSelectState?.imageWidth || !magicSelectState?.imageHeight) {
      return null;
    }
    
    const { bounds, imageWidth, imageHeight } = magicSelectState;
    
    // Calculate scale factor from natural image size to rendered size
    const scaleX = canvasSize.width / imageWidth;
    const scaleY = canvasSize.height / imageHeight;
    
    return {
      left: bounds.x * scaleX,
      top: bounds.y * scaleY,
      width: bounds.width * scaleX,
      height: bounds.height * scaleY,
    };
  }, [magicSelectState, canvasSize]);

  const mediaWrapperRect = mediaWrapperRef.current?.getBoundingClientRect() || null;
  const imageBoundsRect =
    mediaWrapperRect && imageBounds.width && imageBounds.height
      ? {
          left: mediaWrapperRect.left + imageBounds.left,
          top: mediaWrapperRect.top + imageBounds.top,
          width: imageBounds.width,
          height: imageBounds.height,
          right: mediaWrapperRect.left + imageBounds.left + imageBounds.width,
          bottom: mediaWrapperRect.top + imageBounds.top + imageBounds.height,
        }
      : null;

  return (
    <div
      className="canvas-placeholder editing-keyframe-canvas"
      ref={containerRef}
      style={{ cursor: isVersionPreview ? 'default' : getCursorStyle(), pointerEvents: isVersionPreview ? 'none' : 'auto' }}
      onClick={isVersionPreview ? undefined : handleCanvasClick}
    >
      {/* Version preview banner */}
      {isVersionPreview && (
        <div className="editing-keyframe-version-preview-banner">
          Previewing v{previewingKeyframeVersionNumber}
        </div>
      )}

      {/* Single keyframe display when no pending changes - FULL CANVAS */}
      {/* Keep mounted during generation so we can save canvas state before it's cleared */}
      {displayUrl && (
        <div
          className="editing-keyframe-full-canvas"
        >
          <div 
            className="editing-keyframe-media-wrapper" 
            ref={mediaWrapperRef}
            onMouseDown={!uiBlockedForInteraction ? handleLassoMouseDown : undefined}
            onMouseMove={!uiBlockedForInteraction ? handleLassoMouseMove : undefined}
            onMouseUp={!uiBlockedForInteraction ? handleLassoMouseUp : undefined}
            onMouseLeave={!uiBlockedForInteraction ? handleLassoMouseUp : undefined}
            style={{
              pointerEvents: uiBlockedForInteraction ? 'none' : 'auto',
              ...(layoutHoldSize
                ? { minWidth: layoutHoldSize.width, minHeight: layoutHoldSize.height }
                : {}),
            }}
          >
            <img
              ref={imageRef}
              key={`${displayUrl || 'empty'}::${displayUrlRevision}`}
              src={displayUrl}
              alt="Keyframe"
              className="editing-keyframe-full-image"
              style={{
                ...imageFilterStyle,
                ...(fittedImageSize ? { width: fittedImageSize.width, height: fittedImageSize.height } : {}),
                ...(isWorldExploring ? { visibility: 'hidden' } : {}),
              }}
              onLoad={initializeCanvas}
            />

            {/* World Labs 3D world explorer overlay */}
            {isWorldExploring && worldExplorerData?.spzUrl && (
              <Suspense fallback={<div className="world-explorer-loading">Loading 3D engine...</div>}>
                <WorldExplorerOverlay
                  key={`world-${keyframeId}-${worldExplorerData.spzUrl}`}
                  ref={worldExplorerOverlayRef}
                  spzUrl={worldExplorerData.spzUrl}
                  containerWidth={fittedImageSize?.width}
                  containerHeight={fittedImageSize?.height}
                  onCapture={onWorldCapture}
                  onCancel={onWorldExplorerCancel}
                  isCapturing={isWorldCapturing}
                />
              </Suspense>
            )}
            
            {/* Same overlay as image generation: world gen, capture/regen, and keyframe generate/commit */}
            {showKeyframeGeneratingOverlay && (
              <div className="editing-keyframe-generating-overlay" />
            )}
            
            {/* Canvas overlay for drawing/erasing - positioned to match rendered image bounds */}
            {canvasSize.width > 0 && !uiBlockedForInteraction && (
              <canvas
                ref={canvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                className="editing-keyframe-canvas-overlay"
                style={{
                  pointerEvents: isCanvasActive ? 'auto' : 'none',
                  left: imageBounds.left,
                  top: imageBounds.top,
                  width: imageBounds.width,
                  height: imageBounds.height,
                }}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
              />
            )}

            {/* Lasso selection overlay - positioned to match rendered image bounds */}
            {(isLassoDrawing || magicSelectState?.isSearching) && lassoPath.length > 1 && (
              <svg
                className="editing-keyframe-lasso-overlay"
                style={{ 
                  position: 'absolute',
                  top: imageBounds.top,
                  left: imageBounds.left,
                  width: imageBounds.width,
                  height: imageBounds.height,
                  pointerEvents: 'none',
                  zIndex: 150,
                  overflow: 'visible',
                }}
              >
                <path
                  d={getLassoPathString()}
                  fill="var(--color-bg-user-bubble)"
                  stroke="var(--color-text-secondary)"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            
            {/* Center snap guides */}
            {(snapLines.showVertical || snapLines.showHorizontal) && (
              <div
                className="editing-keyframe-snap-guides"
                style={{
                  left: imageBounds.left,
                  top: imageBounds.top,
                  width: imageBounds.width,
                  height: imageBounds.height,
                }}
              >
                {snapLines.showVertical && (
                  <div
                    className="editing-keyframe-snap-line editing-keyframe-snap-line-vertical"
                    style={{ left: snapLines.centerX }}
                  />
                )}
                {snapLines.showHorizontal && (
                  <div
                    className="editing-keyframe-snap-line editing-keyframe-snap-line-horizontal"
                    style={{ top: snapLines.centerY }}
                  />
                )}
              </div>
            )}

            {/* Text layers - keep visible during generation/commit, but disable interactions */}
            {textLayers.map((layer) => (
              <TextBox
                key={layer.id}
                layer={layer}
                isSelected={selectedTextLayerId === layer.id}
                onSelect={() => {
                  setSelectedTextLayerId(layer.id);
                  setSelectedImageLayerId(null);
                }}
                onUpdate={(updatedLayer) => {
                  const newLayers = textLayers.map((l) => 
                    l.id === updatedLayer.id ? updatedLayer : l
                  );
                  onTextLayersChange(newLayers);
                }}
                onDelete={(layerId) => {
                  const newLayers = textLayers.filter((l) => l.id !== layerId);
                  // Use onTextLayerDeleted which also pushes to history
                  onTextLayerDeleted(newLayers);
                  if (selectedTextLayerId === layerId) {
                    setSelectedTextLayerId(null);
                  }
                }}
                onTransformComplete={handleLayerTransformComplete}
                onTransformStart={handleLayerTransformStart}
                containerBounds={imageBoundsRect}
                parentContainerBounds={mediaWrapperRect}
                style={{ pointerEvents: uiBlockedForInteraction ? 'none' : 'auto' }}
              />
            ))}

            {/* Image layers - keep visible during generation/commit, but disable interactions */}
            {imageLayers.map((layer) => (
              <ImageLayer
                key={layer.id}
                layer={layer}
                isSelected={selectedImageLayerId === layer.id}
                onSelect={() => {
                  setSelectedImageLayerId(layer.id);
                  setSelectedTextLayerId(null);
                }}
                onUpdate={(updatedLayer) => {
                  const newLayers = imageLayers.map((l) => 
                    l.id === updatedLayer.id ? updatedLayer : l
                  );
                  onImageLayersChange(newLayers);
                }}
                onDelete={(layerId) => {
                  const newLayers = imageLayers.filter((l) => l.id !== layerId);
                  // Use onImageLayerDeleted which also pushes to history
                  onImageLayerDeleted(newLayers);
                  if (selectedImageLayerId === layerId) {
                    setSelectedImageLayerId(null);
                  }
                }}
                onEdit={onImageLayerEdit}
                isEditing={editingImageLayerId === layer.id}
                onTransformComplete={handleLayerTransformComplete}
                onTransformStart={handleLayerTransformStart}
                containerBounds={imageBoundsRect}
                parentContainerBounds={mediaWrapperRect}
                style={{ pointerEvents: uiBlockedForInteraction ? 'none' : 'auto' }}
              />
            ))}

            {/* Magic Select: Loading spinner while searching */}
            {magicSelectState?.isSearching && magicSelectState?.clickPosition && (
              <div 
                className="magic-select-ripple-container"
                style={{
                  left: magicSelectState.clickPosition.x,
                  top: magicSelectState.clickPosition.y,
                }}
              >
                <div className="magic-select-spinner" />
              </div>
            )}

            {/* Magic Select: Merged union mask overlay when selections exist */}
            {magicSelectState?.selections?.length > 0 && magicSelectState?.mergedMaskOverlayUrl && (
              <>
                {/* Single merged mask overlay - union of all selections */}
                <div
                  className="magic-select-mask-container"
                  style={{
                    position: 'absolute',
                    left: imageBounds.left,
                    top: imageBounds.top,
                    width: imageBounds.width,
                    height: imageBounds.height,
                  }}
                >
                  {/* Orange fill overlay */}
                  <img 
                    src={magicSelectState.mergedMaskOverlayUrl}
                    alt="Selection mask"
                    className="magic-select-mask-image"
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%', 
                      height: '100%',
                      objectFit: 'fill',
                    }}
                  />
                  {/* Animated glare sweep - masked to selection shape */}
                  <div 
                    className="magic-select-mask-glare"
                    style={{
                      WebkitMaskImage: `url(${magicSelectState.mergedMaskOverlayUrl})`,
                      maskImage: `url(${magicSelectState.mergedMaskOverlayUrl})`,
                      WebkitMaskSize: '100% 100%',
                      maskSize: '100% 100%',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                    }}
                  />
                  {/* Animated marching ants border */}
                  <div className="magic-select-mask-border">
                    <img 
                      src={magicSelectState.mergedMaskOverlayUrl}
                      alt=""
                      aria-hidden="true"
                    />
                  </div>
                </div>

                {/* Floating action menu - hide while searching for new selection */}
                {!magicSelectState.selectedAction && !magicSelectState?.isSearching && (
                  <div className="magic-select-actions" onClick={(e) => e.stopPropagation()}>
                    <button 
                      className="magic-select-action-btn magic-select-action-duplicate"
                      onClick={onMagicSelectDuplicate}
                      title="Duplicate selection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                      <span>Duplicate</span>
                    </button>
                    <button
                      className="magic-select-action-btn magic-select-action-cut"
                      onClick={onMagicSelectCut}
                      title="Cut selection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="6" cy="6" r="3"/>
                        <circle cx="6" cy="18" r="3"/>
                        <line x1="20" y1="4" x2="8.12" y2="15.88"/>
                        <line x1="14.47" y1="14.48" x2="20" y2="20"/>
                        <line x1="8.12" y1="8.12" x2="12" y2="12"/>
                      </svg>
                      <span>Cut</span>
                    </button>
                    <button 
                      className="magic-select-action-btn magic-select-action-edit"
                      onClick={() => onMagicSelectAction('edit')}
                      title="Edit selection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      <span>Edit</span>
                    </button>
                    <button 
                      className="magic-select-action-btn magic-select-action-delete"
                      onClick={onMagicSelectDelete}
                      title="Delete selection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6"/>
                        <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
                      </svg>
                      <span>Delete</span>
                    </button>
                    <button 
                      className="magic-select-action-btn magic-select-action-cancel"
                      onClick={onMagicSelectClear}
                      title="Clear selection"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                )}

                {/* Edit prompt input */}
                {magicSelectState.selectedAction === 'edit' && (
                  <div className="magic-select-edit-container" onClick={(e) => e.stopPropagation()}>
                    <div className="magic-select-edit-input-wrapper">
                      <input
                        type="text"
                        className={`magic-select-edit-input${isEnhancingEditPrompt ? ' enhancing' : ''}`}
                        placeholder="Describe the edit..."
                        value={magicSelectState.editPrompt || ''}
                        onChange={(e) => onMagicSelectEditPromptChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && magicSelectState.editPrompt?.trim() && !isEnhancingEditPrompt) {
                            handleMagicSelectEditSubmitWithPrefix();
                          } else if (e.key === 'Escape') {
                            onMagicSelectAction(null);
                          }
                        }}
                        disabled={isEnhancingEditPrompt}
                        autoFocus
                      />
                      {isEnhancingEditPrompt && (
                        <div className="magic-select-edit-enhancing-overlay">
                          <div className="magic-select-edit-enhancing-shimmer" />
                        </div>
                      )}
                    </div>
                    <div className="magic-select-edit-actions">
                      <button
                        type="button"
                        className="magic-select-edit-enhance"
                        title="Enhance prompt"
                        disabled={!magicSelectState.editPrompt?.trim() || isEnhancingEditPrompt}
                        onClick={handleEnhanceEditPrompt}
                      >
                        {isEnhancingEditPrompt ? 'Enhancing…' : 'Enhance'}
                      </button>
                      <button 
                        className="magic-select-edit-submit"
                        onClick={handleMagicSelectEditSubmitWithPrefix}
                        disabled={!magicSelectState.editPrompt?.trim() || isEnhancingEditPrompt}
                      >
                        Apply
                      </button>
                      <button 
                        className="magic-select-edit-cancel"
                        onClick={() => onMagicSelectAction(null)}
                        disabled={isEnhancingEditPrompt}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Magic Select: Duplicated layer preview - now handled by ImageLayer components */}

            {/* Magic Select: Cutout preview (shows grid pattern where deleted) */}
            {magicSelectState?.cutoutImageUrl && (
              <div
                className="magic-select-cutout-container"
                style={{
                  left: imageBounds.left,
                  top: imageBounds.top,
                  width: imageBounds.width,
                  height: imageBounds.height,
                }}
              >
                <div className="magic-select-cutout-grid" />
                <img 
                  src={magicSelectState.cutoutImageUrl}
                  alt="Cutout preview"
                  className="magic-select-cutout-image"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="editing-keyframe-error">
          {error}
          </div>
        )}

      {/* Confirmation modal for pending changes */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Unsaved Changes"
        message="You have pending changes that haven't been saved. Would you like to save them before leaving?"
        confirmLabel="Save Changes"
        cancelLabel="Discard & Leave"
        onConfirm={handleModalAccept}
        onCancel={handleModalDiscard}
      />
    </div>
  );
});

export default EditingKeyframeView;
